import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'utils/axios';
import { ThemeConfig, ColorPalette } from 'hooks/useThemeConfig';

interface ThemeConfigContextType {
  colorConfig: ColorPalette | null;
  themeConfig: ThemeConfig | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const ThemeConfigContext = createContext<ThemeConfigContextType | null>(null);

interface ThemeConfigProviderProps {
  children: ReactNode;
}

export const ThemeConfigProvider = ({ children }: ThemeConfigProviderProps) => {
  const [themeConfig, setThemeConfig] = useState<ThemeConfig | null>(null);
  const [colorConfigData, setColorConfigData] = useState<ColorPalette | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🎨 [ThemeConfig] Loading theme configuration from API...');
      
      // Call the actual theme config API endpoint
      const hostname = window.location.hostname;
      // Extract subdomain part (e.g., "crane" from "crane.quikstep.io", "dev-manage" from "dev-manage.quikstep.io")
      const domain = hostname.split('.')[0];
      const response = await axios.get(`/utility/config?domain=${encodeURIComponent(domain)}`);
      
      if (response.data?.success && response.data?.data) {
        console.log('🎨 [ThemeConfig] Successfully loaded theme configuration');
        
        // Map the API response to our ThemeConfig interface
        const apiData = response.data.data;
        const mappedConfig: ThemeConfig = {
          name: apiData.name || 'QuickStep',
          icons: {
            logo_path: {
              url: apiData.icons?.logo_path?.url
            },
            logo_light_path: {
              url: apiData.icons?.logo_light_path?.url
            },
            fav_icon_path: {
              url: apiData.icons?.fav_icon_path?.url
            },
            square_logo_path: {
              url: apiData.icons?.square_logo_path?.url
            }
          },
          emailLabel: apiData.emailLabel || 'Email Address',
          emailPlaceholder: apiData.emailPlaceholder || 'Enter email address',
          passwordLabel: apiData.passwordLabel || 'Password',
          passwordPlaceholder: apiData.passwordPlaceholder || 'Enter password',
          forgotPasswordText: apiData.forgotPasswordText || 'Forgot Password?',
          loginButtonText: apiData.loginButtonText || 'Login',
          entraIdButtonText: apiData.entraIdButtonText || 'Login with Microsoft',
          clientConfig: {
            usernamePassword: {
              enabled: apiData.clientConfig?.usernamePassword?.enabled ?? true
            },
            otp: {
              enabled: apiData.clientConfig?.otp?.enabled ?? false
            },
            entraId: {
              enabled: apiData.clientConfig?.entraId?.enabled ?? false
            }
          },
          microsoftAuth: {
            clientId: apiData.clientConfig?.entraId?.clientId,
            tenantId: apiData.clientConfig?.entraId?.tenantId
          },
          // Login-page consent checkboxes + current document versions. Passed through
          // as-is; the login page treats anything missing or malformed as "not shown".
          legal: apiData.legal && typeof apiData.legal === 'object' ? apiData.legal : null
        };
        
        setThemeConfig(mappedConfig);
        setColorConfigData(response?.data?.data?.color_palatte);
      } else {
        console.log('🎨 [ThemeConfig] API returned no data, using default configuration');
        
        // Fallback to default configuration if API doesn't return expected data
        const defaultConfig: ThemeConfig = {
          name: 'QuickStep',
          icons: {
            logo_path: {
              url: undefined // Will use default logo
            },
            logo_light_path: {
              url: undefined // Will use default light logo
            },
            fav_icon_path: {
              url: undefined
            },
            square_logo_path: {
              url: undefined
            }
          },
          emailLabel: 'Email Address',
          emailPlaceholder: 'Enter email address',
          passwordLabel: 'Password',
          passwordPlaceholder: 'Enter password',
          forgotPasswordText: 'Forgot Password?',
          loginButtonText: 'Login',
          entraIdButtonText: 'Login with Microsoft',
          clientConfig: {
            usernamePassword: {
              enabled: true
            },
            otp: {
              enabled: false
            },
            entraId: {
              enabled: false // Default to disabled unless explicitly enabled via API
            }
          },
          microsoftAuth: {
            clientId: undefined,
            tenantId: undefined
          }
        };
        
        setThemeConfig(defaultConfig);
      }
    } catch (err: any) {
      console.error('🎨 [ThemeConfig] Error loading theme configuration:', err);
      
      // Set fallback configuration on error
      const fallbackConfig: ThemeConfig = {
        name: 'QuickStep',
        icons: {
          logo_path: {
            url: undefined
          },
          logo_light_path: {
            url: undefined
          },
          fav_icon_path: {
            url: undefined
          },
          square_logo_path: {
            url: undefined
          }
        },
        emailLabel: 'Email Address',
        emailPlaceholder: 'Enter email address',
        passwordLabel: 'Password',
        passwordPlaceholder: 'Enter password',
        forgotPasswordText: 'Forgot Password?',
        loginButtonText: 'Login',
        entraIdButtonText: 'Login with Microsoft',
        clientConfig: {
          usernamePassword: {
            enabled: true
          },
          otp: {
            enabled: false
          },
          entraId: {
            enabled: false
          }
        },
        microsoftAuth: {
          clientId: undefined,
          tenantId: undefined
        }
      };

      setThemeConfig(fallbackConfig);
      setError(`Failed to load theme configuration: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('🎨 [ThemeConfig] Provider mounted, initializing...');
    loadConfig();
  }, []);

  const refetch = () => {
    loadConfig();
  };

  return (
    <ThemeConfigContext.Provider 
      value={{
        colorConfig: colorConfigData,
        themeConfig,
        loading,
        error,
        refetch
      }}
    >
      {children}
    </ThemeConfigContext.Provider>
  );
};

export const useThemeConfigContext = () => {
  const context = useContext(ThemeConfigContext);
  if (!context) {
    throw new Error('useThemeConfigContext must be used within a ThemeConfigProvider');
  }
  return context;
};

export default ThemeConfigContext;
